import { Inject, Injectable } from '@angular/core';
import {
    EnterpriseLegalAgreementsService, GenerateAgreementsFullRequestParams, GenerateAgreementsResponse, IsbExperienceService, isbExperienceServiceToken,
    MoneyProcessingService, PayorInfo, StoreAgreementAcknowledgmentsRequestParams, StoreAgreementAcknowledgmentsResponse, MoneyProcessingServiceToken, BankCard
} from '@nationwide/dgs-angular-billing-common';
import { catchError, mergeMap, forkJoin, map, Observable, of, retry, tap, timeout } from 'rxjs';
import { environment } from '../../../../../../../environments/environment';
import { DistributionPartnerRoleInfoAdapter } from '../../../../../../billing/shared/distribution-partner-role-info/distribution-partner-role-info.adapter';
import { DistributionPartnerRoleInfoService } from '../../../../../../billing/shared/distribution-partner-role-info/distribution-partner-role-info.service';
import { LoggerService } from '../../../../../../shared/logger/logger.service';
import { submitPaymetCardTypeMap } from '../../../../../../shared/payments/card-utils/card-type.enum';
import { SessionService } from '../../../../../../shared/session/session.service';
import { SetupAutomaticPaymentsFlowService } from '../services/setup-automatic-payments-flow.service';
import { SetupAutomaticPaymentsReview } from './setup-automatic-payments-review.model';
import { NuxeoService } from '../../../../../../shared/service/nuxeo/nuxeo.service';
import { BankCardPaymentItem } from '../../../../shared/models/bankcard-item.model';
import { dpimLoggerCostant } from '../../../../../../billing/shared/distribution-partner-role-info/logger.constant';

@Injectable()
export class SetupAutomaticPaymentsReviewService {

    wasAutoPayAuthorizationFormSuccessfullyGenerated: boolean;

    constructor(
        @Inject(isbExperienceServiceToken) private isbExperience: IsbExperienceService,
        @Inject(MoneyProcessingServiceToken) private moneyProcessing: MoneyProcessingService,
        protected sessionService: SessionService,
        private setupAutomaticPaymentFlowService: SetupAutomaticPaymentsFlowService,
        private readonly setupAutomaticPaymentReview: SetupAutomaticPaymentsReview,
        private distributionPartnerRoleInfoService: DistributionPartnerRoleInfoService,
        private distributionPartnerRoleInfoAdapter: DistributionPartnerRoleInfoAdapter,
        @Inject('logger') private readonly logger: LoggerService,
        private enterpriseLegalAgreementsService: EnterpriseLegalAgreementsService,
        private nuxeoService: NuxeoService
    ) {
    }

    getAutomaticPaymentsAuthorizationForm(): Observable<GenerateAgreementsResponse> {
        const params: GenerateAgreementsFullRequestParams = this.nuxeoService.buildGenerateAgreementsFullPayload(this.setupAutomaticPaymentFlowService.selectedPayMethod, false);
        return this.enterpriseLegalAgreementsService.generateAgreements(params).pipe(
            timeout(environment.TIMEOUT.apiCallDuration.enterpriseLegalAgreements),
            retry(1),
            catchError((error) => {
                this.logger.error('Setup Automatic Payments Review - generate authorization agreement failed', { error });
                throw of(error);
            })
        );
    }

    storeAgreementAcknowledgementsForRecurring(): Observable<StoreAgreementAcknowledgmentsResponse> {
        const params: StoreAgreementAcknowledgmentsRequestParams = this.nuxeoService.buildStoreAgreementAcknowledgementsPayload(this.setupAutomaticPaymentFlowService.selectedPayMethod, false);
        return this.enterpriseLegalAgreementsService.storeAgreementAcknowledgments(params).pipe(
            tap(() => this.setupAutomaticPaymentFlowService.wasRecurringAuthorizationFormSuccessfullySaved = true),
            timeout(environment.TIMEOUT.apiCallDuration.enterpriseLegalAgreements),
            retry(1),
            catchError((error) => {
                this.logger.error('Setup Automatic Payments Review - save authorization agreement failed', { error });
                this.setupAutomaticPaymentFlowService.wasRecurringAuthorizationFormSuccessfullySaved = false;
                throw of(error);
            })
        );
    }

    createAutomaticPaymentReviewManager(): Observable<SetupPaymentResponse> {
        return this.requestPlaceHolder().pipe(
            mergeMap(() =>
                forkJoin({
                    createAutomaticPaymentMethod: this.createAutomaticPaymentMethod(),
                    distributionPartnerRoleInfoResponse: this.distributionPartnerRoleInfoService.fetchDistributionPartnerRoleInfo()
                })
            ),
            tap((responses) => {
                this.distributionPartnerRoleInfoAdapter.mapDistributionPartnerRoleResponse(responses.distributionPartnerRoleInfoResponse, dpimLoggerCostant.SETUP_AUTOMATIC_PAYMENT);
            }),
            mergeMap((previousCalls) =>
                forkJoin({
                    createAutomaticPaymentMethod: of(previousCalls.createAutomaticPaymentMethod),
                    nbpEmailSentSuccessfully: this.sessionService.billingSystem === 'NBP' ? this.sendNbpEmail() : this.requestPlaceHolder()
                })
            )
        );
    }

    createAutomaticPaymentMethod(): Observable<number> {
        const {
            isBankAccountFlow,
            bankName,
            routingNumber,
            bankAccountNumber,
            bankAccountType,
            maskedBankAccountNumber,
            isBankCardFlow,
            billingCycleDueDate,
            isNewPayMethod,
            paymentMethodId
        } = this.setupAutomaticPaymentReview;

        return this.billingService.patchPayPlan({
            billingSystem: this.sessionService.billingSystem,
            accessToken: this.sessionService.accessToken,
            agreementNumber: this.sessionService.billingAccountNumber,
            payload: {
                billingPayload: {
                    accountDueDate: billingCycleDueDate,
                    action: 'add',
                    requestType: 'BillingMethodChange.PayPlanUpdate',
                    enterpriseCustomerNumber: this.sessionService.ecn,
                    currentBillingMethodType: 'Direct',
                    newBillingMethodType: isBankAccountFlow ? 'Recurring EFT' : 'Recurring Bankcard',
                    paymentMethodId: paymentMethodId || undefined,
                    paymentMethod: {
                        preferredMethod: this.setupAutomaticPaymentFlowService.selectedPayMethod.isDefault,
                        description: 'TEST',
                        payorInfo: this.sessionService.billingSystem === 'SAP Billing' || this.setupAutomaticPaymentReview.isBankAccountFlow ? undefined : this.payorInfo,
                        paymentMethodType: isBankAccountFlow ? 'ElectronicFundsTransfer.PaymentMethod' : 'BankCard.PaymentMethod',
                        electronicFundsTransfer: isBankAccountFlow ? {
                            bankName,
                            bankRoutingNumber: routingNumber,
                            bankAccountNumber,
                            bankAccountType,
                            maskedBankAccountNumber: isNewPayMethod ? maskedBankAccountNumber : undefined
                        } : undefined,
                        bankCard: isBankCardFlow ? <any> this.getBankCard() : undefined
                    }
                }
            }
        }).pipe(
            tap(() => this.setupAutomaticPaymentFlowService.save({ successfullyEnrolled: true })),
            timeout(environment.TIMEOUT.apiCallDuration.submitPayment),
            catchError((error) => {
                this.setupAutomaticPaymentFlowService.save({ successfullyEnrolled: false, errorDetails: { errorMessage: error.error?.developerMessage, errorCode: error.status } });
                this.logger.error('API ERROR: SETUP AUTOMATIC PAYMENTS SUBMISSION', error);
                return of(error.status);
            })
        );
    }

    deleteCamCache(): void {
        this.isbExperience.deleteCamCache({
            ecn: this.sessionService.ecn,
            accessToken: this.sessionService.accessToken
        }).subscribe({
            next: (response) => {
                this.logger.info('CAM Cache deleted successfully in Enroll Auto Pay flow', response);
            },
            error: (error) => {
                this.logger.error('CAM Cache deletion failed in Enroll Auto Pay flow', {
                    error: error.error || error
                });
            }
        });
    }

    sendNbpEmail(): Observable<boolean> {
        let response: Observable<boolean>;

        if (this.setupAutomaticPaymentFlowService.successfullyEnrolled) {
            response = this.isbExperience.sendEnrollAutomaticPaymentsEmail({
                accessToken: this.sessionService.accessToken,
                billingSystem: this.sessionService.billingSystem,
                omsData: {
                    omsServiceTransformationStrategyData: {
                        servicePlusIndicator: this.sessionService.dpimServicePlusIndicator,
                        agentPhoneNumber: this.sessionService.dpimAgentPhoneNumber
                    }
                }
            }).pipe(
                map(() => true),
                catchError(() => of(false))
            );
        } else {
            response = this.requestPlaceHolder();
        }

        return response;
    }

    private getBankCard(): BankCard {
        const bankCard: BankCard = {
            encryptedPan: undefined,
            encryptedPanKey: undefined,
            encryptedPanPubKeyFingerPrint: undefined,
            expirationDate: this.fetchExpirationDate(this.setupAutomaticPaymentReview.cardExpirationYear, this.setupAutomaticPaymentReview.cardExpirationMonth),
            ccLastFour: (<BankCardPaymentItem> this.setupAutomaticPaymentFlowService.selectedPayMethod.item).cardNumberLastFour,
            cardBrand: <'MasterCard' | 'American Express' | 'Visa' | 'Discover'>submitPaymetCardTypeMap.get(this.setupAutomaticPaymentReview.cardType)
        };
        if (this.sessionService.billingSystem === 'NBP') {
            bankCard.profileId = (<BankCardPaymentItem> this.setupAutomaticPaymentFlowService.selectedPayMethod.item).profileId;
            bankCard.encryptedPan = '';
        }
        return bankCard;
    }

    private requestPlaceHolder(): Observable<null> {
        return of(null);
    }

    private fetchExpirationDate(cardExpirationYear: string, cardExpirationMonth: string): string {
        return this.sessionService.billingSystem === 'SAP Billing' ?
            `${cardExpirationYear}${cardExpirationMonth}` : `${cardExpirationYear}-${cardExpirationMonth}`;
    }

    private get billingService(): IsbExperienceService | MoneyProcessingService {
        let service: IsbExperienceService | MoneyProcessingService;
        if (this.sessionService.billingSystem === 'NBP') {
            service = this.moneyProcessing;
        } else {
            service = this.isbExperience;
        }
        return service;
    }

    private get payorInfo(): PayorInfo {
        return {
            firstName: this.setupAutomaticPaymentReview.firstName,
            middleName: this.setupAutomaticPaymentReview.middleInitial,
            lastName: this.setupAutomaticPaymentReview.lastName,
            fullName: this.setupAutomaticPaymentReview.payorName,
            addressLine1: this.setupAutomaticPaymentReview.street,
            addressLine2: this.setupAutomaticPaymentReview.apartmentNumber,
            city: this.setupAutomaticPaymentReview.city,
            state: this.setupAutomaticPaymentReview.stateName,
            postalCode: this.setupAutomaticPaymentReview.zip
        };
    }
}

export interface SetupPaymentResponse {
    createAutomaticPaymentMethod?: number;
    updateEmailAddress?: boolean | null;
    nbpEmailSentSuccessfully?: boolean | null;
}
